<template>
  <div class="layout">
    <nav-bar
      :logoSrc="$maxxpm.config.logo.src"
      :logoAlt="$maxxpm.config.logo.alt"
      :bannerTitle="$maxxpm.config.nav.bannerTitle"
      :bannerSubtitle="$maxxpm.config.nav.bannerSubtitle"
      :bannerCTA="$maxxpm.config.nav.bannerCTA"
    />
    <div
      v-if="promoItems.length"
      id="incentive"
      class="promo-carousel"
    >
      <div class="promo-carousel-content">
        <p class="promo-message">{{ currentPromoMessage }}</p>
        <Button
          label="View Availability"
          class="promo-cta"
          @click="router.push('/availability')"
        />
      </div>
      <div
        v-if="promoItems.length > 1"
        class="promo-dots"
      >
        <button
          v-for="(item, index) in promoItems"
          :key="item.id"
          type="button"
          class="promo-dot"
          :class="{ active: index === promoIndex }"
          :aria-label="`Promotion ${index + 1}`"
          @click="setPromoIndex(index)"
        />
      </div>
      <p class="promo-disclaimer">*Offer subject to availability. Contact leasing for more details.</p>
    </div>
    <main>
      <router-view />
      <Dialog
        v-if="$maxxpm.config.hasShowingPopup"
        v-model:visible="showPopup"
        :modal="true"
        :dismissableMask="true"
        :style="{ width: '50vw' }"
        :breakpoints="{ '960px': '75vw', '641px': '95vw' }"
      >
        <template #header>
          <h2 class="text-center flex-grow-1">Don't wait, schedule to visit the property today!</h2>
        </template>
        <div class="flex justify-content-center p-4 popup-showing">
          <Button
            @click="openShowingPopup"
            aria-haspopup="dialog"
            label="Schedule a Showing"
          ></Button>
        </div>
      </Dialog>
      <Dialog
        v-if="$maxxpm.config.hasFlyerPopup"
        v-model:visible="showPopup"
        :modal="true"
        :dismissableMask="true"
        :closable="true"
        :style="{ width: 'auto', maxWidth: '500px', padding: '0' }"
        :breakpoints="{ '960px': '75vw', '641px': '95vw' }"
        :contentStyle="{ padding: '0' }"
        class="flyer-popup"
      >
        <img
          :src="$maxxpm.config.flyerImage"
          :alt="$maxxpm.config.flyerAlt"
          class="flyer-popup-image"
        />
      </Dialog>
    </main>
    <footer>
      <app-footer
        :communityName="$maxxpm.config.communityName"
        :streetAddress="$maxxpm.config.footer.address"
        :cityStateZip="$maxxpm.config.footer.cityStateZip"
        :phone="$maxxpm.config.footer.phone"
        :logoUrl="$maxxpm.config.footer.logo ?? $maxxpm.config.logo.src"
        :copyrightYear="$maxxpm.config.footer.copyrightYear"
      />
    </footer>
  </div>
</template>

<script setup>
import { inject, ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import bookingMessageHandler from "./utils/booking_message_handler";

const $maxxpm = inject("$maxxpm");
const router = useRouter();

const promoItems = ref([]);
const promoIndex = ref(0);
const isMobile = ref(false);
let promoRotateTimer = null;

function dedupePromotions(listings) {
  const seen = new Map();
  for (const listing of listings) {
    for (const promo of listing.promotions ?? []) {
      if (!seen.has(promo.id)) {
        seen.set(promo.id, promo);
      }
    }
  }
  return Array.from(seen.values());
}

async function loadPromotions() {
  const apiUrl = process.env.VUE_APP_LISTINGS_API_URL;
  if (!apiUrl) return;
  try {
    const { data } = await axios.get(`${apiUrl}/listings?property_group=${$maxxpm.config.availability.propertyGroup}`);
    promoItems.value = dedupePromotions(data);
  } catch {
    promoItems.value = [];
  }
}

function updateIsMobile() {
  isMobile.value = window.matchMedia("(max-width: 768px)").matches;
}

const currentPromoMessage = computed(() => {
  const item = promoItems.value[promoIndex.value];
  if (!item) return "";
  return isMobile.value ? item.badge ?? item.description ?? "" : item.description ?? item.badge ?? "";
});

function setPromoIndex(index) {
  promoIndex.value = index;
  startPromoRotation();
}

function startPromoRotation() {
  if (promoRotateTimer) {
    clearInterval(promoRotateTimer);
    promoRotateTimer = null;
  }
  if (promoItems.value.length <= 1) return;
  promoRotateTimer = setInterval(() => {
    promoIndex.value = (promoIndex.value + 1) % promoItems.value.length;
  }, 5000);
}

const showPopup = ref(false);
const Calendly = ref(null);
const QRG = ref(null);

const getListingAvailableForShowing = async () => {
  const listings = await axios.get(`${process.env.VUE_APP_LISTINGS_API_URL}/listings?property_group=${$maxxpm.config.availability.propertyGroup}`);
  if (listings.data.length) {
    const availableForShowing = listings.data.find((listing) => {
      if (listing.microsoftBookingServiceId || listing.community.microsoftBookingServiceId || listing.showingUrl || listing.community.showingUrl) {
        if (listing.readyForShowingOn) {
          return new Date(listing.readyForShowingOn) < new Date();
        } else if (listing.activeUnit && listing.activeUnit.readyForShowingOn) {
          return new Date(listing.activeUnit.readyForShowingOn) < new Date();
        } else if (listing.activeUnit && listing.activeUnit.availableOn) {
          const availableOn = new Date(listing.activeUnit.availableOn);
          const sevenDaysBeforeAvailableOn = new Date(availableOn.getTime());
          sevenDaysBeforeAvailableOn.setUTCDate(availableOn.getUTCDate() - 7);
          return sevenDaysBeforeAvailableOn <= new Date();
        }
      }
      return false;
    });
    return availableForShowing;
  }
  return null;
};

let urlParams;
const availableForShowing = ref(null);

async function openShowingPopup() {
  if (availableForShowing.value) {
    if (availableForShowing.value.microsoftBookingServiceId || availableForShowing.value.community.microsoftBookingServiceId) {
      QRG.value.open({ communityId: availableForShowing.value.community.id, listingId: availableForShowing.value.id });
    } else if (availableForShowing.value.showingUrl) {
      Calendly.value.initPopupWidget({
        url: availableForShowing.value.showingUrl,
        utm: {
          utmSource: urlParams.get("utm_source") || "haydenfarmstownhomes.com",
          utmMedium: "listing",
          utmContent: availableForShowing.value.id,
        },
      });
    } else {
      Calendly.value.initPopupWidget({
        url: availableForShowing.value.community.showingUrl,
        utm: {
          utmSource: urlParams.get("utm_source") || "haydenfarmstownhomes.com",
          utmMedium: "listing",
          utmContent: availableForShowing.value.id,
        },
      });
    }
  }
}

onMounted(async () => {
  updateIsMobile();
  window.addEventListener("resize", updateIsMobile);
  loadPromotions().then(() => startPromoRotation());

  Calendly.value = window.Calendly;
  const cname = "showPopup";
  QRG.value = window.QrgBooking;
  window.addEventListener("message", bookingMessageHandler);
  urlParams = new URLSearchParams(window.location.search);
  availableForShowing.value = await getListingAvailableForShowing();

  setTimeout(() => {
    if (((availableForShowing.value && $maxxpm.config.hasShowingPopup) || $maxxpm.config.hasFlyerPopup) && !document.cookie.split("; ").find((c) => c.startsWith(cname))) {
      document.cookie = `${cname}=true; max-age=${60 * 60 * 24 * 2}`;
      showPopup.value = true;
    }
  }, 3500);
});

onUnmounted(() => {
  window.removeEventListener("resize", updateIsMobile);
  if (promoRotateTimer) {
    clearInterval(promoRotateTimer);
  }
  window.removeEventListener("message", bookingMessageHandler);
});
</script>

<style>
#app {
  font-family: Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}

.layout {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  position: relative;
}

body {
  margin: 0;
}

.img-fluid {
  max-width: 100%;
  height: auto;
}

.flyer-popup .p-dialog-content {
  padding: 0 !important;
  border-radius: 0;
}

.flyer-popup .p-dialog-header {
  padding: 0.5rem;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  background: transparent;
}

.flyer-popup .p-dialog-header .p-dialog-header-icon {
  color: #333;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
}

.flyer-popup-image {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 6px;
}

.promo-carousel {
  background: var(--primary-color);
  color: #fff;
  padding: 0.45rem 1rem 0.3rem;
}

.promo-carousel-content {
  max-width: min(1200px, 95vw);
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.promo-message {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.promo-cta.p-button {
  background: #fff !important;
  color: var(--primary-color) !important;
  border: none !important;
  font-weight: 700;
  padding: 0.35rem 0.85rem !important;
}

.promo-dots {
  display: flex;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.35rem;
}

.promo-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.45);
}

.promo-dot.active {
  background: #fff;
}

.promo-disclaimer {
  text-align: center;
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
  opacity: 0.95;
}

@media (max-width: 768px) {
  .promo-carousel-content {
    flex-direction: column;
  }

  .promo-disclaimer {
    font-size: 0.8rem;
  }
}
</style>
